'use client'

import React, { useState, useEffect, useCallback } from 'react'
import Image from 'next/image'
import { useTheme } from '../theme/provider'
import { ChevronLeft, ChevronRight } from 'lucide-react'

export interface CarouselSlide {
  id: string | number
  image?: string
  title: string
  excerpt?: string
  category?: string
  formattedDate?: string
  articleUrl?: string
  content?: React.ReactNode
}

export interface HeroCarouselProps {
  slides: CarouselSlide[]
  autoPlay?: boolean
  autoPlayInterval?: number
  height?: string
  className?: string
  onSlideChange?: (index: number) => void
}

/**
 * HeroCarousel - Real Madrid style carousel component
 * Based on the Real Madrid website hero carousel design
 *
 * Features from Real Madrid:
 * - 600px height with gradient overlays
 * - Red category badge
 * - Blur background behind content
 * - Image zoom on hover
 * - Auto-play with pause on hover
 * - Backdrop blur navigation buttons
 */
export function HeroCarousel({
  slides = [],
  autoPlay = false,
  autoPlayInterval = 5000,
  height = '600px',
  className = '',
  onSlideChange,
}: HeroCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const { theme } = useTheme()

  const nextSlide = useCallback(() => {
    if (!slides || slides.length === 0) return
    setCurrentSlide((prev) => {
      const next = (prev + 1) % slides.length
      onSlideChange?.(next)
      return next
    })
  }, [slides, onSlideChange])

  const prevSlide = useCallback(() => {
    if (!slides || slides.length === 0) return
    setCurrentSlide((prev) => {
      const next = (prev - 1 + slides.length) % slides.length
      onSlideChange?.(next)
      return next
    })
  }, [slides, onSlideChange])

  // Auto-play effect (Real Madrid style: pause on hover)
  useEffect(() => {
    if (!autoPlay || isHovered) return

    const interval = setInterval(nextSlide, autoPlayInterval)
    return () => clearInterval(interval)
  }, [autoPlay, autoPlayInterval, isHovered, nextSlide])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prevSlide()
      if (e.key === 'ArrowRight') nextSlide()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [prevSlide, nextSlide])

  if (!slides || slides.length === 0) {
    return (
      <div
        className={`flex items-center justify-center rounded-2xl border ${className}`}
        style={{
          height,
          backgroundColor: theme.colors.surface,
          borderColor: theme.colors.border,
          color: theme.colors.textSecondary,
        }}
      >
        No slides available
      </div>
    )
  }

  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${className}`}
      style={{ height }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Slides */}
      <div className="relative w-full h-full">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-all duration-700 ease-in-out ${
              index === currentSlide
                ? 'opacity-100'
                : 'opacity-0'
            }`}
          >
            {slide.content ? (
              <div className="w-full h-full">{slide.content}</div>
            ) : (
              <a className="group block" href={slide.articleUrl || '#'}>
                <article className="relative h-[450px] rounded-2xl overflow-hidden border border-gray-200">
                  {/* Background Image */}
                  {slide.image && (
                    <Image
                      src={slide.image}
                      alt={slide.title}
                      fill
                      priority={index === 0}
                      quality={70}
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 1120px"
                      className="object-cover object-center group-hover:scale-105 transition-transform duration-700"
                      placeholder="blur"
                      blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwMCIgaGVpZ2h0PSI2MDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyMDAiIGhlaWdodD0iNjAwIiBmaWxsPSIjZjNmNGY2Ii8+PC9zdmc+"
                    />
                  )}

                  {/* Content */}
                  <div className="absolute inset-0 flex items-end">
                    <div className="w-full px-8 pt-8 pb-4 md:px-12 md:pt-12 md:pb-6">
                      <div className="relative">
                        {/* Backdrop Blur Background */}
                        <div className="absolute -inset-3 bg-black/40 backdrop-blur-md rounded-2xl" />

                        {/* Content */}
                        <div className="relative z-10 p-2">
                          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white line-clamp-3 drop-shadow-2xl mb-1.5">
                            {slide.title}
                          </h2>
                          {slide.category && (
                            <span className="inline-block px-2 py-0.5 text-[9px] font-bold bg-[#FEBE10] text-[#001F3F] rounded">
                              {slide.category.toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Hover Arrow Icon */}
                  <div className="absolute top-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </div>
                  </div>
                </article>
              </a>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Buttons (Real Madrid style: backdrop blur with white/transparent) */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 rounded-full p-3 transition-all duration-300 hover:bg-white/20 z-20"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
            }}
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 rounded-full p-3 transition-all duration-300 hover:bg-white/20 z-20"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
            }}
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </>
      )}
    </div>
  )
}
